// OTA Releases — TypeScript types and the apps/channels static config.
//
// Two orthogonal concepts:
//   • **Release channel** — where a version sits in the rollout ladder (beta → general).
//   • **Android signing key** — which APK variant a device installs (standard vs platform).
//
// Both signed builds for the same version code normally share one release-channel pointer.

export type OtaAppId = 'vn' | 'th' | 'vt' | 'va' | 'vc' | 'launcher';

export interface OtaApp {
  id: OtaAppId;
  packageId: string;
  name: string;
  color: string;
  bg: string;
  riskNotes: string;
}

export const OTA_APPS: readonly OtaApp[] = [
  { id: 'vn',       packageId: 'com.vortexmarine.vortexnav',     name: 'VortexNav',     color: '#22d3ee', bg: 'bg-cyan-500/10 text-cyan-400',       riskNotes: 'flagship — highest-volume app' },
  { id: 'th',       packageId: 'com.vortexmarine.truehelm',      name: 'TrueHelm',      color: '#34d399', bg: 'bg-emerald-500/10 text-emerald-400', riskNotes: 'auto-helm control' },
  { id: 'vt',       packageId: 'com.vortexmarine.vortextide',    name: 'VortexTide',    color: '#60a5fa', bg: 'bg-blue-500/10 text-blue-400',       riskNotes: 'tides & currents' },
  { id: 'va',       packageId: 'com.vortexmarine.vortexanchor',  name: 'VortexAnchor',  color: '#f59e0b', bg: 'bg-amber-500/10 text-amber-400',     riskNotes: 'anchor watch' },
  { id: 'vc',       packageId: 'com.vortexmarine.vortexcompass', name: 'VortexCompass', color: '#a78bfa', bg: 'bg-violet-500/10 text-violet-400',   riskNotes: 'celestial + mag' },
  { id: 'launcher', packageId: 'com.vortexmarine.launcher',      name: 'Launcher',      color: '#f87171', bg: 'bg-red-500/10 text-red-400',         riskNotes: 'highest risk — doubled soak' },
] as const;

/** Device rollout channel (beta → general; alpha reserved for future). */
export type OtaReleaseChannelId = 'beta' | 'general';

/** @deprecated Prefer OtaReleaseChannelId */
export type OtaChannelId = OtaReleaseChannelId;

export interface OtaChannel {
  id: OtaReleaseChannelId;
  label: string;
  description: string;
}

export const OTA_CHANNELS: readonly OtaChannel[] = [
  {
    id: 'beta',
    label: 'Beta',
    description: 'Early test fleet — new builds land here first',
  },
  {
    id: 'general',
    label: 'General release',
    description: 'General availability; optional gradual rollout',
  },
] as const;

export type BuildStatus = 'stable' | 'testing' | 'candidate' | 'archived';

/** Which Android signing key was used to produce the APK. */
export type AndroidSigningKey = 'standard' | 'platform';

/**
 * R2 artifact signing variant from the API (`app-signed` = standard key, `platform-signed` = platform key).
 * Named `channel` on API responses for historical reasons — not a release channel.
 */
export type OtaSigningChannel = 'app-signed' | 'platform-signed';

export interface Build {
  /** API / R2 bucket (use signingKey in UI). */
  channel: OtaSigningChannel;
  signingKey: AndroidSigningKey;
  appDir: string;
  versionDir: string;
  packageId: string;
  shortName: string;
  versionCode: number;
  versionName: string;
  sha256: string;
  sizeBytes: number;
  releaseNotes: string;
  apkKey: string;
  manifestKey: string;
  uploadedAt: string;
  /** Promoted on this artifact's signing manifest in R2. */
  isPromoted: boolean;
  /** False when the R2 manifest is missing, empty, unparseable, or missing required fields. */
  isValid: boolean;
  /** Human-readable reason the build is invalid; null when valid. */
  error: string | null;
}

export type RolloutPolicy = 'exposure' | 'open';

export interface ChannelPointer {
  channel: OtaReleaseChannelId;
  app: OtaAppId;
  current: number;
  previous: number;
  served: number;
  maxDevices: number | null;
  minHealthyHours: number;
  healthyHours: number;
  promotedBy: string;
  promotedAt: string;
  policy: RolloutPolicy;
  haltOnRollback: boolean;
  status: 'healthy' | 'rolling' | 'halted' | 'no-pointer';
  allowList?: string[];
  denyList?: string[];
}

export type DeviceConnectivity = 'wifi' | 'cellular' | 'satellite' | 'none';
export type DeviceRole = 'user' | 'tester' | 'tester_plus' | 'admin';

export interface OtaDevice {
  id: string;
  name: string;
  vessel: string;
  cluster: string;
  lat: number;
  lng: number;
  role: DeviceRole;
  connectivity: DeviceConnectivity;
  channel: OtaReleaseChannelId;
  vnVc: number;
  model: string;
  sog: number;
  underway: boolean;
  offlineDays: number;
  crashes: number;
  lastCheckIn: string;
  autoRollback: boolean;
  flaggedBuild?: number;
}

export type AuditAction =
  | 'promote'
  | 'rollback'
  | 'halt'
  | 'resume'
  | 'widen-rollout'
  | 'prune-build';

export interface AuditEntry {
  t: string;
  actor: string;
  action: AuditAction;
  app: OtaAppId;
  channel: OtaReleaseChannelId | null;
  from: number | null;
  to: number | null;
  reason: string;
  smoke: 'pass' | 'fail' | null;
}

export const appOf = (id: OtaAppId): OtaApp | undefined =>
  OTA_APPS.find((a) => a.id === id);

// ---- OTA folder structure status (GET /api/admin/ota/status) ----

export interface OtaPointerStatus {
  key: string;
  channel: OtaSigningChannel;
  exists: boolean;
  isValid: boolean;
  appCount: number;
  updatedAt: string | null;
  error: string | null;
}

export interface OtaFolderFileStatus {
  exists: boolean;
  sizeBytes: number;
}

export type OtaFolderHealth = 'healthy' | 'degraded' | 'broken';

/** Status of one build folder: `apks/<app>/<versionCode>/<variant>/`. */
export interface OtaFolderStatus {
  appDir: string;
  versionDir: string;
  channel: OtaSigningChannel;
  prefix: string;
  apk: OtaFolderFileStatus;
  manifest: OtaFolderFileStatus;
  manifestValid: boolean;
  manifestError: string | null;
  packageIds: string[];
  hasSha256: boolean;
  isPromoted: boolean;
  uploadedAt: string | null;
  health: OtaFolderHealth;
}

export interface OtaMissingVariant {
  appDir: string;
  versionDir: string;
  missing: OtaSigningChannel;
}

export interface OtaStructureStatus {
  scannedAt: string;
  pointers: OtaPointerStatus[];
  legacyPointerKeys: string[];
  folders: OtaFolderStatus[];
  missingVariants: OtaMissingVariant[];
  strayKeys: string[];
  summary: {
    folders: number;
    healthy: number;
    degraded: number;
    broken: number;
  };
}
